import React, { useState } from 'react';
import { Card, Form, Button, Row, Col, Tab, Nav, Alert } from 'react-bootstrap';
import { FaSave, FaStore, FaShippingFast, FaCreditCard, FaCog } from 'react-icons/fa';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Mock settings state - in a real app, these would be fetched from an API
  const [generalSettings, setGeneralSettings] = useState({
    storeName: 'My Amazing Store',
    storeEmail: 'contact@myamazingstore.com',
    customerSupport: '+1 (555) 123-4567',
    defaultLanguage: 'en',
    defaultCurrency: 'usd'
  });

  const [storeSettings, setStoreSettings] = useState({
    productsPerPage: 12,
    enableReviews: true,
    enableWishlist: true,
    enableCompare: false,
    showOutOfStock: true,
    taxRate: 8.5,
    metaTitle: 'My Amazing Store - The Best Products Online',
    metaDescription: 'Shop the best products at amazing prices with fast shipping and excellent customer service.'
  });

  const [shippingMethods, setShippingMethods] = useState([
    { id: 1, name: 'Standard Shipping', price: 5.99, estimatedDays: '3-5', isActive: true },
    { id: 2, name: 'Express Shipping', price: 12.99, estimatedDays: '1-2', isActive: true },
    { id: 3, name: 'Free Shipping', price: 0, threshold: 50, estimatedDays: '5-7', isActive: true }
  ]);

  const [paymentMethods, setPaymentMethods] = useState([
    { id: 1, name: 'Credit Card', isActive: true },
    { id: 2, name: 'PayPal', isActive: true },
    { id: 3, name: 'Apple Pay', isActive: false },
    { id: 4, name: 'Google Pay', isActive: false }
  ]);

  const handleGeneralChange = (e) => {
    const { name, value } = e.target;
    setGeneralSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStoreChange = (e) => {
    const { name, value, type, checked } = e.target;
    setStoreSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleShippingChange = (id, field, value) => {
    setShippingMethods(prev => 
      prev.map(method => 
        method.id === id ? { ...method, [field]: value } : method
      )
    );
  };

  const handlePaymentToggle = (id) => {
    setPaymentMethods(prev => 
      prev.map(method => 
        method.id === id ? { ...method, isActive: !method.isActive } : method
      )
    );
  };

  const handleSave = () => {
    // In a real app, this would send the settings to an API
    console.log('Saving settings:', {
      generalSettings,
      storeSettings,
      shippingMethods,
      paymentMethods
    });
    
    // Show success message
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  return (
    <Card className="shadow-sm mb-4">
      <Card.Body>
        <h4 className="card-title mb-4">Store Settings</h4>
        
        {saveSuccess && (
          <Alert variant="success" className="mb-4">
            Settings saved successfully!
          </Alert>
        )}
        
        <Tab.Container 
          id="settings-tabs" 
          activeKey={activeTab} 
          onSelect={(k) => setActiveTab(k)}
        >
          <Row>
            <Col md={3}>
              <Nav variant="pills" className="flex-column mb-4">
                <Nav.Item>
                  <Nav.Link eventKey="general" className="d-flex align-items-center">
                    <FaCog className="me-2" /> General
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="store" className="d-flex align-items-center">
                    <FaStore className="me-2" /> Store Settings
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="shipping" className="d-flex align-items-center">
                    <FaShippingFast className="me-2" /> Shipping Methods
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="payment" className="d-flex align-items-center">
                    <FaCreditCard className="me-2" /> Payment Methods
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
            <Col md={9}>
              <Tab.Content>
                <Tab.Pane eventKey="general">
                  <h5 className="mb-3">General Settings</h5>
                  <Form>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Store Name</Form.Label>
                          <Form.Control 
                            type="text" 
                            name="storeName" 
                            value={generalSettings.storeName}
                            onChange={handleGeneralChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Store Email</Form.Label>
                          <Form.Control 
                            type="email" 
                            name="storeEmail" 
                            value={generalSettings.storeEmail}
                            onChange={handleGeneralChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Customer Support Phone</Form.Label>
                          <Form.Control 
                            type="text" 
                            name="customerSupport" 
                            value={generalSettings.customerSupport}
                            onChange={handleGeneralChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Default Language</Form.Label>
                          <Form.Select 
                            name="defaultLanguage" 
                            value={generalSettings.defaultLanguage}
                            onChange={handleGeneralChange}
                          >
                            <option value="en">English</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                            <option value="de">German</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label>Default Currency</Form.Label>
                      <Form.Select 
                        name="defaultCurrency" 
                        value={generalSettings.defaultCurrency}
                        onChange={handleGeneralChange}
                      >
                        <option value="usd">USD - US Dollar</option>
                        <option value="eur">EUR - Euro</option>
                        <option value="gbp">GBP - British Pound</option>
                        <option value="cad">CAD - Canadian Dollar</option>
                      </Form.Select>
                    </Form.Group>
                  </Form>
                </Tab.Pane>
                
                <Tab.Pane eventKey="store">
                  <h5 className="mb-3">Store Settings</h5>
                  <Form>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Products Per Page</Form.Label>
                          <Form.Control 
                            type="number" 
                            name="productsPerPage" 
                            value={storeSettings.productsPerPage}
                            onChange={handleStoreChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Tax Rate (%)</Form.Label>
                          <Form.Control 
                            type="number" 
                            name="taxRate" 
                            value={storeSettings.taxRate}
                            onChange={handleStoreChange}
                            step="0.01"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Form.Group className="mb-3">
                      <Form.Check 
                        type="checkbox"
                        id="enableReviews"
                        label="Enable Product Reviews"
                        name="enableReviews"
                        checked={storeSettings.enableReviews}
                        onChange={handleStoreChange}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Check 
                        type="checkbox"
                        id="enableWishlist"
                        label="Enable Wishlist Feature"
                        name="enableWishlist"
                        checked={storeSettings.enableWishlist}
                        onChange={handleStoreChange}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Check 
                        type="checkbox"
                        id="enableCompare"
                        label="Enable Product Comparison"
                        name="enableCompare"
                        checked={storeSettings.enableCompare}
                        onChange={handleStoreChange}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Check 
                        type="checkbox"
                        id="showOutOfStock"
                        label="Show Out of Stock Products"
                        name="showOutOfStock"
                        checked={storeSettings.showOutOfStock}
                        onChange={handleStoreChange}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Meta Title</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="metaTitle" 
                        value={storeSettings.metaTitle}
                        onChange={handleStoreChange}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Meta Description</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={3} 
                        name="metaDescription" 
                        value={storeSettings.metaDescription}
                        onChange={handleStoreChange}
                      />
                    </Form.Group>
                  </Form>
                </Tab.Pane>
                
                <Tab.Pane eventKey="shipping">
                  <h5 className="mb-3">Shipping Methods</h5>
                  
                  {shippingMethods.map((method) => (
                    <Card key={method.id} className="mb-3">
                      <Card.Body>
                        <Row className="align-items-center">
                          <Col>
                            <Form.Group className="mb-2">
                              <Form.Label>Method Name</Form.Label>
                              <Form.Control 
                                type="text" 
                                value={method.name}
                                onChange={(e) => handleShippingChange(method.id, 'name', e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Form.Group className="mb-2">
                              <Form.Label>Price ($)</Form.Label>
                              <Form.Control 
                                type="number" 
                                value={method.price}
                                onChange={(e) => handleShippingChange(method.id, 'price', parseFloat(e.target.value))}
                                step="0.01"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Form.Group className="mb-2">
                              <Form.Label>Est. Days</Form.Label>
                              <Form.Control 
                                type="text" 
                                value={method.estimatedDays}
                                onChange={(e) => handleShippingChange(method.id, 'estimatedDays', e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                          <Col md="auto">
                            <Form.Group className="mb-2 mt-4">
                              <Form.Check 
                                type="switch"
                                id={`shipping-active-${method.id}`}
                                label="Active"
                                checked={method.isActive}
                                onChange={(e) => handleShippingChange(method.id, 'isActive', e.target.checked)}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        {method.price === 0 && (
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-0">
                                <Form.Label>Free Shipping Threshold ($)</Form.Label>
                                <Form.Control 
                                  type="number" 
                                  value={method.threshold || 0}
                                  onChange={(e) => handleShippingChange(method.id, 'threshold', parseFloat(e.target.value))}
                                  step="0.01"
                                />
                                <Form.Text muted>
                                  Minimum order amount to qualify for free shipping
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                </Tab.Pane>
                
                <Tab.Pane eventKey="payment">
                  <h5 className="mb-3">Payment Methods</h5>
                  
                  {paymentMethods.map((method) => (
                    <Card key={method.id} className="mb-3">
                      <Card.Body>
                        <Row className="align-items-center">
                          <Col>
                            <h6 className="mb-0">{method.name}</h6>
                          </Col>
                          <Col md="auto">
                            <Form.Check 
                              type="switch"
                              id={`payment-active-${method.id}`}
                              label="Active"
                              checked={method.isActive}
                              onChange={() => handlePaymentToggle(method.id)}
                            />
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
        
        <div className="d-flex justify-content-end mt-4">
          <Button 
            variant="primary" 
            onClick={handleSave}
            className="d-flex align-items-center"
          >
            <FaSave className="me-2" /> Save Settings
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default Settings; 